<script setup>
import { computed, defineProps, defineEmits } from "vue"
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue"
import { XMarkIcon } from "@heroicons/vue/24/outline"

const props = defineProps({
  modelValue: Boolean,
  size: {
    type: String,
    required: false,
    default: "max-w-lg",
  },
  padding: {
    type: String,
    required: false,
    default: "px-4 pt-5 pb-4 sm:p-6",
  },
  zIndex: {
    type: String,
    required: false,
    default: "z-50",
  },
  closeable: Boolean,
})

const emit = defineEmits(["update:modelValue", "close"])

const open = computed({
  get() {
    return props.modelValue
  },

  set(value) {
    return emit("update:modelValue", value)
  },
})
</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative" :class="zIndex" @close="open = false">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto" :class="zIndex">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              :class="[
                'sm:' + size,
                padding,
                'max-w-xs relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full',
              ]"
            >
              <div
                v-if="closeable"
                class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block"
              >
                <button
                  type="button"
                  class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="open = false"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <slot></slot>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
